import { fromLatLon as latLonToUtm } from 'utm'
import { point } from '@turf/turf'
import { toMercator, toWgs84 } from '@turf/projection'
import type { Feature, Point, Polygon, Position } from 'geojson'
import type { LngLat as MapboxLngLat } from '@/lib/map-x/webmap-viewer-types'
import { warn } from '@/lib/logger'

export type LngLat = Pick<MapboxLngLat, 'lng' | 'lat'>

export const DECIMAL = 'decimal'
export const DMS = 'dms' /* degrees-minutes-seconds */
export const UTM = 'utm' /* universal transverse mercator */
export const DDM = 'ddm' /* degrees-decimal-minutes */
export const NORTH_SYM = 'N'
export const SOUTH_SYM = 'S'
export const EAST_SYM = 'E'
export const WEST_SYM = 'W'

export interface BBox {
  top: number
  bottom: number
  left: number
  right: number
}

interface DMSLngLat {
  longitude: DMS
  latitude: DMS
}

interface DMS {
  hemisphere: string
  degrees: number
  minutes: number
  seconds: number
}

interface DDMLngLat {
  longitude: DDM
  latitude: DDM
}

interface DDM {
  hemisphere: string
  degrees: number
  minutes: number
}

/**
 * Constrains a given longitude to be within -180 and 180 degrees.
 */
const constrainLongitude = (longitude: number) => {
  if (longitude < -180) {
    return ((longitude - 180) % 360) + 180
  } else if (longitude > 180) {
    return ((longitude + 180) % 360) - 180
  }

  return longitude
}

/**
 * Convert a HMS to decimal
 * - hours - degree hours.
 * - minutes - degree minutes.
 * - seconds - degree seconds.
 * - direction - direction (E, W, N, or S)
 */
const hmsToDecimal = (
  hours: number,
  minutes: number,
  seconds: number,
  direction: string
) => {
  let decimal = hours + minutes / 60 + seconds / 3600

  if (['s', 'w'].includes(direction.toLowerCase())) {
    decimal *= -1
  }
  return decimal
}

const decimalCoordinateFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 5,
})

// Input validators
/**
 * *(AI-generated)*
 * Returns true if the given latitude value is valid (between -90 and 90).
 */
export const isValidLatitude = (lat: number) => lat >= -90 && lat <= 90

/**
 * *(AI-generated)*
 * Returns true if the given longitude value is valid (between -180 and 180).
 */
export const isValidLongitude = (lon: number) => lon >= -180 && lon <= 180

/**
 * *(AI-generated)*
 * Returns true if the given height value is valid (between 0 and 22).
 */
export const isValidHeight = (ht: number) => ht >= 0 && ht <= 22

// Default zoom level for fallback
export const ZOOM_DEFAULT = 15

/**
 * *(AI-generated)*
 * Returns a bounding box from a GeoJSON visible region polygon feature.
 */
export const bboxFromVisibleRegion = (
  visibleRegion: Feature<Polygon>
): BBox | null => {
  if (!visibleRegion?.geometry?.coordinates) {
    return null
  }

  const { coordinates } = visibleRegion.geometry
  const topLeft = coordinates[0][0]
  const bottomRight = coordinates[0][2]

  const left = topLeft[0]
  const right = bottomRight[0]
  const top = topLeft[1]
  const bottom = bottomRight[1]

  // Normalize to ensure top > bottom and right > left
  return {
    top: Math.max(top, bottom),
    bottom: Math.min(top, bottom),
    left: Math.min(left, right),
    right: Math.max(left, right),
  }
}

/**
 * A geographic location in degrees.  Provides helper functions to convert to
 * useful coordinate systems.  All coordinate transformations for this object
 * should assume a WGS84 datum is used.  If another datum is used, then a datum
 * transformation is needed in addition to a coordinate transformation.
 */
export default class Geographic {
  /**
   * Longitude in decimal degrees.  Will be between -180 and 180.
   */
  readonly longitude: number

  /**
   * Latitude in decimal degrees.  Will be between -90 and 90
   */
  readonly latitude: number

  /**
   * Map zoom level.  Will be between 0 and 22
   */
  readonly height: number

  /**
   * Initializes a Geographic in degrees (EPSG:4326).
   * - longitude - Longitude in degrees. Must be between -180 and 180
   * - latitude - Latitude in degrees. Must be between -90 and 90
   * - height - Zoom: Must be between 0 and 22
   */
  constructor(longitude = 0, latitude = 0, height = 0) {
    longitude = constrainLongitude(longitude)

    if (!isValidLatitude(latitude)) {
      throw new Error('Invalid latitude value: Must be between -90 and 90')
    }
    if (!isValidLongitude(longitude)) {
      throw new Error('Invalid longitude value: Must be between -180 and 180')
    }
    if (!isValidHeight(height)) {
      height = ZOOM_DEFAULT
      warn(
        'Logger',
        `Invalid height value: Must be between 0 and 22. Using ZOOM_DEFAULT of ${ZOOM_DEFAULT}`
      )
    }

    this.longitude = longitude
    this.latitude = latitude
    this.height = height

    // Geographics are immutable use copy method to get a new point
    Object.freeze(this)
  }

  /**
   * Creates a Geographic from turf point.
   */
  static fromTurfPoint([longitude, latitude]: Position) {
    return new Geographic(longitude, latitude)
  }

  /**
   * Creates a Geographic from given location in degrees.
   * - longitude - Longitude location in degrees.
   * - latitude - Latitude location in degrees.
   * - height - Height (altitude) in meters.
   */
  static fromDegrees(longitude: number, latitude: number, height = 0) {
    return new Geographic(longitude, latitude, height)
  }

  /**
   * Creates a Geographic from given location as a tuple of [lon, lat] in
   *  degrees.
   */
  static fromDegreesTuple([longitude, latitude]: Position) {
    return new Geographic(longitude, latitude)
  }

  /**
   * Creates a Geographic from a given location in Pseudo Mercator coordinates
   * (EPSG:3857).
   * - x - X-coordinate position.
   * - y - Y-coordinate position.
   */
  static fromMercator(x: number, y: number) {
    const {
      geometry: {
        coordinates: [lon, lat],
      },
    } = toWgs84(point([x, y]))

    return new Geographic(lon, lat)
  }

  /**
   * Helper function that creates a Geographic from a given coordinate tuple
   * ordered as [x, y].
   */
  static fromMercatorTuple([x, y]: Position) {
    return Geographic.fromMercator(x, y)
  }

  /**
   * Creates a Geographic from given location in degrees, minutes, seconds.
   * - lonH - Longitude location in degree hours.
   * - lonM - Longitude location in degree minutes.
   * - lonS - Longitude location in degree seconds.
   * - lonDir - Longitude direction (E or W)
   * - latH - Latitude location in degree hours.
   * - latM - Latitude location in degree minutes.
   * - latS - Latitude location in degree seconds.
   * - latDir - Latitude direction (N or S)
   */
  static fromDms(
    lonH: number,
    lonM: number,
    lonS: number,
    lonDir: string,
    latH: number,
    latM: number,
    latS: number,
    latDir: string
  ) {
    return new Geographic(
      hmsToDecimal(lonH, lonM, lonS, lonDir),
      hmsToDecimal(latH, latM, latS, latDir)
    )
  }

  /**
   * Creates a Geographic from given location in degrees, minutes, and seconds, with extensive error checking.
   * If location cannot be parsed successfully, or is equal to null island, this function returns null.
   * - lonH - Longitude location in degree hours.
   * - lonM - Longitude location in degree minutes.
   * - lonS - Longitude location in degree seconds.
   * - lonDir - Longitude direction (E or W)
   * - latH - Latitude location in degree hours.
   * - latM - Latitude location in degree minutes.
   * - latS - Latitude location in degree seconds.
   * - latDir - Latitude direction (N or S)
   */
  static fromDmsChecked(
    lonH: number,
    lonM: number,
    lonS: number,
    lonDir: string,
    latH: number,
    latM: number,
    latS: number,
    latDir: string
  ) {
    try {
      // check longitude direction
      lonDir = lonDir?.toLowerCase()[0]
      if (lonDir !== 'e' && lonDir !== 'w') {
        return null
      }

      // check latitude direction
      latDir = latDir?.toLowerCase()[0]
      if (latDir !== 'n' && latDir !== 's') {
        return null
      }

      // check longitude
      if (
        lonH < 0 ||
        lonH > 180 ||
        lonM < 0 ||
        lonM >= 60 ||
        lonS < 0 ||
        lonS >= 60
      ) {
        return null
      }

      // check latitude
      if (
        latH < 0 ||
        latH > 85.0511 ||
        latM < 0 ||
        latM >= 60 ||
        latS < 0 ||
        latS >= 60
      ) {
        return null
      }

      // parse location
      const location = Geographic.fromDms(
        lonH,
        lonM,
        lonS,
        lonDir,
        latH,
        latM,
        latS,
        latDir
      )

      // ensure location is not null island
      if (
        Math.abs(location.longitude) < 0.001 &&
        Math.abs(location.latitude) < 0.001
      ) {
        return null
      }

      return location
    } catch (e) {
      return null
    }
  }

  /**
   * Creates a Geographic from given location in degrees and decimal minutes.
   * - lonH - Longitude location in degree hours.
   * - lonM - Longitude location in degree minutes, decimal format.
   * - lonDir - Longitude direction (E or W)
   * - latH - Latitude location in degree hours.
   * - latM - Latitude location in degree minutes, decimal format.
   * - latDir - Latitude direction (N or S)
   */
  static fromDdm(
    lonH: number,
    lonM: number,
    lonDir: string,
    latH: number,
    latM: number,
    latDir: string
  ) {
    return new Geographic(
      hmsToDecimal(lonH, lonM, 0, lonDir),
      hmsToDecimal(latH, latM, 0, latDir)
    )
  }

  /**
   * Convert user facing coord back to one we can use
   * - urlCoordinate - user facing coordinate string in URL formatted
   *  "latitude,longitude" to the 5th decimal degree
   */
  static fromUrlCoordinate(urlCoordinate: string) {
    if (!urlCoordinate) {
      return null
    }

    const [latitude, longitude, zoom] = urlCoordinate.split(',')

    const parsedLon = parseFloat(longitude)
    const parsedLat = parseFloat(latitude)
    const parsedHeight = parseFloat(zoom)

    return new Geographic(
      parsedLon,
      parsedLat,
      isNaN(parsedHeight) ? ZOOM_DEFAULT : parsedHeight
    )
  }

  /**
   * Create a Geographic from a Mapbox event coordinate object.
   */
  static fromMapbox(lngLat: LngLat) {
    return new Geographic(lngLat.lng, lngLat.lat)
  }

  /**
   * Returns this geographic location as GeoJSON
   */
  toGeoJSON(): Point {
    return {
      type: 'Point',
      coordinates: this.toDegreesTuple(),
    }
  }

  /**
   * Returns this geographic location as a longitude and latitude tuple in degrees.
   */
  toDegreesTuple<T extends Position>(): T {
    return [this.longitude, this.latitude] as T
  }

  /**
   * Returns this geographic location as a longitude, latitude, and height tuple in degrees.
   */
  toDegreesTupleWithHeight<T extends Position>(): T {
    return [this.longitude, this.latitude, this.height] as T
  }

  /**
   * Returns a coordinate string for use as query route url coord param
   */
  toURLCoordParam() {
    return `${this.latitude.toFixed(6)},${this.longitude.toFixed(6)}`
  }

  /**
   * Returns a coordinate string for use as query route url coord param, including height value
   */
  toURLCoordParamWithHeight() {
    return `${this.latitude.toFixed(6)},${this.longitude.toFixed(6)},${this.height}`
  }

  /**
   * Returns this geographic location as an x & y position in Pseudo Mercator
   * coordinates (EPSG:3857).
   */
  toMercator(): { x: number; y: number } {
    const {
      geometry: {
        coordinates: [x, y],
      },
    } = toMercator(point([this.longitude, this.latitude]))
    return { x, y }
  }

  /**
   * Returns this geographic location in degrees, minutes, seconds.
   */
  toDms(): DMSLngLat {
    const lonHemisphere = this.longitude >= 0 ? EAST_SYM : WEST_SYM
    const absLon = Math.abs(this.longitude)
    const lonDegrees = Math.floor(absLon)
    const lonMinSec = (absLon - lonDegrees) * 60
    const lonMinutes = Math.floor(lonMinSec)
    const lonSeconds = (lonMinSec - lonMinutes) * 60

    const latHemisphere = this.latitude >= 0 ? NORTH_SYM : SOUTH_SYM
    const absLat = Math.abs(this.latitude)
    const latDegrees = Math.floor(absLat)
    const latMinSec = (absLat - latDegrees) * 60
    const latMinutes = Math.floor(latMinSec)
    const latSeconds = (latMinSec - latMinutes) * 60

    return {
      longitude: {
        hemisphere: lonHemisphere,
        degrees: lonDegrees,
        minutes: lonMinutes,
        seconds: lonSeconds,
      },
      latitude: {
        hemisphere: latHemisphere,
        degrees: latDegrees,
        minutes: latMinutes,
        seconds: latSeconds,
      },
    }
  }

  /**
   * Returns this geographic location in degrees and decimal minutes
   */
  toDdm(): DDMLngLat {
    const lonHemisphere = this.longitude >= 0 ? EAST_SYM : WEST_SYM
    const absLon = Math.abs(this.longitude)
    const lonDegrees = Math.floor(absLon)
    const lonMinSec = (absLon - lonDegrees) * 60
    const lonMinDecimal = Math.abs(lonMinSec)

    const latHemisphere = this.latitude >= 0 ? NORTH_SYM : SOUTH_SYM
    const absLat = Math.abs(this.latitude)
    const latDegrees = Math.floor(absLat)
    const latMinSec = (absLat - latDegrees) * 60
    const latMinDecimal = Math.abs(latMinSec)

    return {
      longitude: {
        hemisphere: lonHemisphere,
        degrees: lonDegrees,
        minutes: lonMinDecimal,
      },
      latitude: {
        hemisphere: latHemisphere,
        degrees: latDegrees,
        minutes: latMinDecimal,
      },
    }
  }

  /**
   * Returns this geographic location in Universal Transverse Mercator
   *  projection.
   */
  toUtm() {
    const { easting, northing, zoneNum, zoneLetter } = latLonToUtm(
      this.latitude,
      this.longitude
    )

    return {
      easting,
      northing,
      zoneNum,
      zoneLetter,
    }
  }

  /**
   * Returns turf.js point http://turfjs.org/docs/#point
   */
  toTurfPoint(): Feature<Point> {
    return point(this.toDegreesTuple())
  }

  /**
   * Returns a string in the decimal pattern
   */
  toDegreesString() {
    return `${decimalCoordinateFormatter.format(this.latitude)}, ${decimalCoordinateFormatter.format(this.longitude)}`
  }

  /**
   * Returns a string in the DMS pattern
   */
  toDmsString() {
    const { longitude, latitude } = this.toDms()
    const latDeg = `${latitude.degrees}°`
    const latMin = `${latitude.minutes}'`
    const latSec = `${latitude.seconds.toFixed(2)}"`
    const latHem = latitude.hemisphere
    const lonDeg = `${longitude.degrees}°`
    const lonMin = `${longitude.minutes}'`
    const lonSec = `${longitude.seconds.toFixed(2)}"`
    const lonHem = longitude.hemisphere
    return (
      `${latDeg} ${latMin} ${latSec} ${latHem} ` +
      `${lonDeg} ${lonMin} ${lonSec} ${lonHem}`
    )
  }

  /**
   * Returns a string in the DDM pattern
   */
  toDdmString() {
    const { longitude, latitude } = this.toDdm()
    const latDeg = `${latitude.degrees}°`
    const latMin = `${latitude.minutes.toFixed(3)}'`
    const latHem = latitude.hemisphere
    const lonDeg = `${longitude.degrees}°`
    const lonMin = `${longitude.minutes.toFixed(3)}'`
    const lonHem = longitude.hemisphere
    return `${latDeg} ${latMin} ${latHem}, ${lonDeg} ${lonMin} ${lonHem}`
  }

  /**
   * Returns a string in the UTM pattern
   */
  toUtmString() {
    const { easting, northing, zoneNum, zoneLetter } = this.toUtm()
    return (
      `${zoneNum}${zoneLetter} ` +
      `${easting.toFixed(2)}m ${northing.toFixed(2)}m`
    )
  }

  /**
   * Gets a formatted coordinate string of a specific type
   * - coordinateType - decimal | dms | utm, coordinate type to get string of
   */
  getCoordinatesStringByType(coordinateType = '') {
    switch (coordinateType) {
      case DMS:
        return this.toDmsString()
      case DDM:
        return this.toDdmString()
      case UTM:
        return this.toUtmString()
      case DECIMAL:
      default:
        return this.toDegreesString()
    }
  }

  /**
   * Compares values of latitude and longitude to determine whether the two Geographic objects
   *  represent the same location.
   * - other - Other Geographic to compare values with.
   * - precision - Number of decimal places to compare
   */
  equals(other: Geographic, precision = 0) {
    if (precision) {
      return (
        this.latitude.toFixed(precision) ===
          other.latitude.toFixed(precision) &&
        this.longitude.toFixed(precision) ===
          other.longitude.toFixed(precision) &&
        this.height.toFixed(precision) === other.height.toFixed(precision)
      )
    }

    return (
      this.latitude === other.latitude &&
      this.longitude === other.longitude &&
      this.height === other.height
    )
  }

  /**
   * Returns a clone of this Geographic object
   */
  clone() {
    return new Geographic(this.longitude, this.latitude, this.height)
  }
}
